import { createEnv } from '@t3-oss/env-core';

import { z } from 'zod';

export const env = createEnv({
  /*
   * Specify what prefix the client-side variables must have.
   * This is enforced both on type-level and at runtime.
   */
  clientPrefix: 'NEXT_PUBLIC',
  server: {
    NODE_ENV: z.string(),
    PORT: z.string().optional(),
    VERCEL_URL: z.string().optional(),
    NEXTAUTH_SECRET: z.string(),
    GOOGLE_CLIENT_ID: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    RESEND_API_KEY: z.string(),
    OPENAI_API_KEY: z.string(),
    UNSPLASH_ACCESS_KEY: z.string(),
    PEXEL_API_KEY: z.string(),
    AMPLIFY_BUCKET: z.string(),
    BUCKET_REGION: z.string(),
    BUCKET_ACCESS_KEY_ID: z.string(),
    BUCKET_SECRET_ACCESS_KEY: z.string(),
    INNGEST_SIGNING_KEY: z.string(),
    INNGEST_EVENT_KEY: z.string(),
    INNGEST_API_KEY: z.string(),
    RECAPTCHA_SECRET: z.string(),
    PUBLIC_URL: z.string(),
    ZAPIER_CLIENT_ID: z.string(),
    ZAPIER_CLIENT_SECRET: z.string(),
    STRIPE_SECRET_KEY: z.string(),
    TYPEFORM_SECRET: z.string(),
    TYPEFORM_WORKSPACE_ID: z.string(),
    POSTHOG_API_KEY: z.string().optional(),
    POSTHOG_PERSONAL_API_KEY: z.string().optional(),
    POSTHOG_PROJECT_ID: z.string().optional(),
    GA4_PROPERTY_ID: z.string().optional(),
    GA4_SERVICE_ACCOUNT_JSON: z.string().optional(),
    GSC_SITE_URL: z.string().optional(),
    GSC_SERVICE_ACCOUNT_JSON: z.string().optional(),
    GROWTH_AGENT_MODEL: z.string().optional(),
    INTEGRATION_SECRET_KEY: z.string().optional(),
    RECITED_API_KEY: z.string().optional(),
    RECITED_API_URL: z.string().optional(),
    RECITED_WEBHOOK_SECRET: z.string().optional(),
    LINKEDIN_CLIENT_ID: z.string().optional(),
    LINKEDIN_CLIENT_SECRET: z.string().optional(),
    /**
     * Optional pin: numeric LinkedIn organization ID (e.g. "12345678") to post
     * as. If unset, we auto-pick the first organization the authenticating
     * member is an admin of — fine for single-tenant admin where there's only
     * one Page (Formsuite). Set this if the admin is admin of multiple Pages.
     */
    LINKEDIN_ORGANIZATION_ID: z.string().optional(),
    TWITTER_CLIENT_ID: z.string().optional(),
    TWITTER_CLIENT_SECRET: z.string().optional(),
    REDDIT_CLIENT_ID: z.string().optional(),
    REDDIT_CLIENT_SECRET: z.string().optional(),
    /**
     * Per-workspace calendar integrations used by the Scheduler form question.
     * Each provider's developer portal needs `<base>/api/calendar/<provider>/callback`
     * registered as an allowed redirect URI.
     *   - Calendly: https://calendly.com/integrations/api_webhooks
     *   - Cal.com:  https://cal.com/settings/developer/api-keys / OAuth client
     *   - Google:   Use a SEPARATE Google Cloud project from the Sign-In client.
     *               Calendar needs the sensitive calendar.events / calendar.readonly
     *               scopes, which force OAuth verification on the consent screen —
     *               keep those off the Sign-In project or new-user Google login
     *               breaks. The Google Calendar adapter REQUIRES
     *               GOOGLE_CALENDAR_CLIENT_ID/SECRET (no fallback to the Sign-In
     *               creds), so calendar can never accidentally use that client.
     */
    CALENDLY_CLIENT_ID: z.string().optional(),
    CALENDLY_CLIENT_SECRET: z.string().optional(),
    CALCOM_CLIENT_ID: z.string().optional(),
    CALCOM_CLIENT_SECRET: z.string().optional(),
    GOOGLE_CALENDAR_CLIENT_ID: z.string().optional(),
    GOOGLE_CALENDAR_CLIENT_SECRET: z.string().optional(),
    /**
     * Dev-only switch for the win-back drip. When set to `1`, every
     * `step.sleep("...d")` in winBackDrip.ts is reinterpreted as seconds
     * instead of days, so a full D7/D21/D45/D90 cycle runs in ~90 seconds.
     * Hard-gated behind `NEXT_PUBLIC_APP_ENV !== 'production'` — leaving it
     * set in prod is a no-op.
     */
    WIN_BACK_FAST_MODE: z.string().optional(),
    /**
     * Dev-only: '1' shrinks the upgrade-nudge email's 30-minute debounce to
     * 1 minute so the flow can be tested quickly. Ignored in production.
     */
    UPGRADE_NUDGE_FAST_MODE: z.string().optional(),
    /**
     * Stable CNAME apex that workspace-owned hostnames (custom domains) point
     * at. Customers add a CNAME record on their hostname pointing here, and
     * the proxy middleware reads the inbound `Host` header to dispatch to the
     * right form. Default is `forms.formsuite.co` — that hostname must be
     * configured in Vercel (or wherever this app is hosted) to terminate TLS
     * and serve this Next app. Override to `app.formsuite.co` (or any other
     * hostname owned by Formsuite that serves this app) if the dedicated
     * `forms.*` subdomain is not yet set up.
     *
     * NOTE: existing `CustomDomain` rows store the value that was active at
     * creation time, so changing this env doesn't break already-verified
     * customers — new customers just get the new target on their next add.
     */
    CUSTOM_DOMAIN_CNAME_TARGET: z.string().optional(),
    /**
     * Vercel "Domains for SaaS" credentials. When set, the custom-domain
     * verifier auto-registers each customer hostname against the project so
     * Vercel terminates TLS and routes inbound traffic into this app. Without
     * these, custom domains stop at DNS — Vercel won't accept the SNI for
     * `<customer>.example.com` and the TLS handshake fails before our
     * middleware runs.
     *
     * - `VERCEL_API_TOKEN`: Personal/team token with "Domains" + "Projects"
     *    write scopes. Create at https://vercel.com/account/tokens.
     * - `VERCEL_PROJECT_ID`: The project (`prj_…`) hosting this Next app.
     * - `VERCEL_TEAM_ID`: Required when the project lives under a team; omit
     *    for hobby/personal accounts.
     *
     * All three are optional so dev/test environments can run without them —
     * the integration short-circuits to a no-op when unconfigured.
     */
    VERCEL_API_TOKEN: z.string().optional(),
    VERCEL_PROJECT_ID: z.string().optional(),
    VERCEL_TEAM_ID: z.string().optional(),
  },
  client: {
    NEXT_PUBLIC_URL: z.string().optional(),
    NEXT_PUBLIC_ENCRTYPTION_SECRET: z.string(),
    NEXT_PUBLIC_GOOGLE_FONTS: z.string(),
    NEXT_PUBLIC_RECAPTCHA: z.string(),
    NEXT_PUBLIC_PRODUCT_ID: z.string(),
    NEXT_PUBLIC_GTM_ID: z.string().optional(),
    NEXT_PUBLIC_APP_ENV: z.string(),
    /**
     * Set to "1"/"true" to expose the Google Calendar scheduler connect flow in
     * production. Off by default because the Google OAuth app is pending
     * verification (Testing mode hard-blocks non-test users). Outside production
     * the flow is always enabled. Flip this + redeploy once Google approves.
     */
    NEXT_PUBLIC_GOOGLE_CALENDAR_CONNECT_ENABLED: z.string().optional(),
    NEXT_PUBLIC_AMPLIFY_BUCKET: z.string(),
    NEXT_PUBLIC_BUCKET_REGION: z.string(),
    NEXT_PUBLIC_FACEBOOK_PIXEL_ID: z.string(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_POSTHOG_KEY: z.string().optional(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string().optional(),
  },
  /**
   * What object holds the environment variables at runtime.
   * Often `procces.env` or `import.meta.env`
   */
  runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    PORT: process.env.PORT,
    NEXT_PUBLIC_URL: process.env.NEXT_PUBLIC_URL,
    PUBLIC_URL: process.env.NEXT_PUBLIC_URL,
    NEXT_PUBLIC_DOMAIN: process.env.NEXT_PUBLIC_DOMAIN,
    NEXT_PUBLIC_SCHEME: process.env.NEXT_PUBLIC_SCHEME,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_CLIENT_ID: process.env.STRIPE_CLIENT_ID,
    NEXT_PUBLIC_PRODUCT_ID: process.env.NEXT_PUBLIC_PRODUCT_ID,
    DATABPASE_PASSWORD: process.env.DATABPASE_PASSWORD,
    DATABASE_URL: process.env.DATABASE_URL,
    DIRECT_URL: process.env.DIRECT_URL,
    NEXT_PUBLIC_BUILDER_API_KEY: process.env.NEXT_PUBLIC_BUILDER_API_KEY,
    NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    NEXT_PUBLIC_ENCRTYPTION_SECRET: process.env.NEXT_PUBLIC_ENCRTYPTION_SECRET,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    UNSPLASH_ACCESS_KEY: process.env.UNSPLASH_ACCESS_KEY,
    PEXEL_API_KEY: process.env.PEXEL_API_KEY,
    NEXT_PUBLIC_GOOGLE_FONTS: process.env.NEXT_PUBLIC_GOOGLE_FONTS,
    AMPLIFY_BUCKET: process.env.AMPLIFY_BUCKET,
    NEXT_PUBLIC_AMPLIFY_BUCKET: process.env.NEXT_PUBLIC_AMPLIFY_BUCKET,
    BUCKET_REGION: process.env.BUCKET_REGION,
    NEXT_PUBLIC_BUCKET_REGION: process.env.NEXT_PUBLIC_BUCKET_REGION,
    BUCKET_ACCESS_KEY_ID: process.env.BUCKET_ACCESS_KEY_ID,
    BUCKET_SECRET_ACCESS_KEY: process.env.BUCKET_SECRET_ACCESS_KEY,
    INNGEST_SIGNING_KEY: process.env.INNGEST_SIGNING_KEY,
    INNGEST_EVENT_KEY: process.env.INNGEST_EVENT_KEY,
    INNGEST_API_KEY: process.env.INNGEST_API_KEY,
    NEXT_PUBLIC_RECAPTCHA: process.env.NEXT_PUBLIC_RECAPTCHA,
    RECAPTCHA_SECRET: process.env.RECAPTCHA_SECRET,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_GOOGLE_CALENDAR_CONNECT_ENABLED: process.env.NEXT_PUBLIC_GOOGLE_CALENDAR_CONNECT_ENABLED,
    ZAPIER_CLIENT_ID: process.env.ZAPIER_CLIENT_ID,
    ZAPIER_CLIENT_SECRET: process.env.ZAPIER_CLIENT_SECRET,
    NEXT_PUBLIC_FACEBOOK_PIXEL_ID: process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID,
    TYPEFORM_SECRET: process.env['TYPEFORM_SECRET'],
    TYPEFORM_WORKSPACE_ID: process.env['TYPEFORM_WORKSPACE_ID'],
    NEXT_PUBLIC_POSTHOG_KEY: process.env['NEXT_PUBLIC_POSTHOG_KEY'],
    NEXT_PUBLIC_POSTHOG_HOST: process.env['NEXT_PUBLIC_POSTHOG_HOST'],
    POSTHOG_API_KEY: process.env['POSTHOG_API_KEY'],
    POSTHOG_PERSONAL_API_KEY: process.env['POSTHOG_PERSONAL_API_KEY'],
    POSTHOG_PROJECT_ID: process.env['POSTHOG_PROJECT_ID'],
    GA4_PROPERTY_ID: process.env['GA4_PROPERTY_ID'],
    GA4_SERVICE_ACCOUNT_JSON: process.env['GA4_SERVICE_ACCOUNT_JSON'],
    GSC_SITE_URL: process.env['GSC_SITE_URL'],
    GSC_SERVICE_ACCOUNT_JSON: process.env['GSC_SERVICE_ACCOUNT_JSON'],
    GROWTH_AGENT_MODEL: process.env['GROWTH_AGENT_MODEL'],
    INTEGRATION_SECRET_KEY: process.env['INTEGRATION_SECRET_KEY'],
    RECITED_API_KEY: process.env['RECITED_API_KEY'],
    RECITED_API_URL: process.env['RECITED_API_URL'],
    RECITED_WEBHOOK_SECRET: process.env['RECITED_WEBHOOK_SECRET'],
    LINKEDIN_CLIENT_ID: process.env['LINKEDIN_CLIENT_ID'],
    LINKEDIN_CLIENT_SECRET: process.env['LINKEDIN_CLIENT_SECRET'],
    LINKEDIN_ORGANIZATION_ID: process.env['LINKEDIN_ORGANIZATION_ID'],
    TWITTER_CLIENT_ID: process.env['TWITTER_CLIENT_ID'],
    TWITTER_CLIENT_SECRET: process.env['TWITTER_CLIENT_SECRET'],
    REDDIT_CLIENT_ID: process.env['REDDIT_CLIENT_ID'],
    REDDIT_CLIENT_SECRET: process.env['REDDIT_CLIENT_SECRET'],
    CALENDLY_CLIENT_ID: process.env['CALENDLY_CLIENT_ID'],
    CALENDLY_CLIENT_SECRET: process.env['CALENDLY_CLIENT_SECRET'],
    CALCOM_CLIENT_ID: process.env['CALCOM_CLIENT_ID'],
    CALCOM_CLIENT_SECRET: process.env['CALCOM_CLIENT_SECRET'],
    GOOGLE_CALENDAR_CLIENT_ID: process.env['GOOGLE_CALENDAR_CLIENT_ID'],
    GOOGLE_CALENDAR_CLIENT_SECRET: process.env['GOOGLE_CALENDAR_CLIENT_SECRET'],
    WIN_BACK_FAST_MODE: process.env['WIN_BACK_FAST_MODE'],
    UPGRADE_NUDGE_FAST_MODE: process.env['UPGRADE_NUDGE_FAST_MODE'],
    CUSTOM_DOMAIN_CNAME_TARGET: process.env['CUSTOM_DOMAIN_CNAME_TARGET'],
    VERCEL_API_TOKEN: process.env['VERCEL_API_TOKEN'],
    VERCEL_PROJECT_ID: process.env['VERCEL_PROJECT_ID'],
    VERCEL_TEAM_ID: process.env['VERCEL_TEAM_ID'],
  },

});
